import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Search, Mail, Phone, Trash2 } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Staff = Database["public"]["Tables"]["staff"]["Row"];

export const Route = createFileRoute("/admin/staff")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Staff — MAKAI" }] }),
  component: StaffPage,
});

function StaffPage() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<Staff[]>([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState<"all" | Database["public"]["Enums"]["school_role"]>("all");

  const load = useCallback(async () => {
    if (!active) return;
    let qb = supabase.from("staff").select("*").eq("school_id", active.school_id).order("last_name");
    if (role !== "all") qb = qb.eq("role_type", role);
    const { data, error } = await qb;
    if (error) toast.error(error.message);
    setRows((data ?? []) as Staff[]);
  }, [active, role]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = `${r.first_name} ${r.last_name} ${r.email ?? ""} ${r.staff_number ?? ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <AdminShell title="Staff" subtitle="Teachers, accountants and administrative staff." action={<StaffDialog onSaved={load} />}>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, staff no." className="pl-9" />
        </div>
        <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
          <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {["admin","principal","teacher","accountant","librarian","support"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No staff yet. Click <b>Add staff</b> to create your first record.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Contact</th><th className="px-4 py-2.5">Staff no.</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-4 py-3 font-semibold">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{s.role_type}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {s.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</div>}
                    {s.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.staff_number ?? "—"}</td>
                  <td className="px-4 py-3"><Badge variant={s.status === "active" ? "default" : "outline"} className="capitalize">{s.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                      if (!confirm("Remove this staff record?")) return;
                      const { error } = await supabase.from("staff").delete().eq("id", s.id);
                      if (error) toast.error(error.message); else load();
                    }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}

function StaffDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", role_type: "teacher" as Database["public"]["Enums"]["school_role"], employment: "full_time" as Database["public"]["Enums"]["staff_employment"], staff_number: "", hire_date: "", qualification: "" });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    if (!form.first_name || !form.last_name) { toast.error("Name required"); return; }
    setBusy(true);
    let staff_number = form.staff_number;
    if (!staff_number) {
      const { data } = await supabase.rpc("next_school_number", { _school: active.school_id, _key: "staff_seq", _prefix: "STF" });
      staff_number = (data as string) || "";
    }
    const { error } = await supabase.from("staff").insert({
      school_id: active.school_id,
      first_name: form.first_name, last_name: form.last_name,
      email: form.email || null, phone: form.phone || null,
      role_type: form.role_type, employment: form.employment, staff_number: staff_number || null,
      hire_date: form.hire_date || null, qualification: form.qualification || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Staff added"); setOpen(false); onSaved();
    setForm({ first_name: "", last_name: "", email: "", phone: "", role_type: "teacher", employment: "full_time", staff_number: "", hire_date: "", qualification: "" });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Add staff</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New staff</DialogTitle></DialogHeader>
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
          <Field label="First name *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></Field>
          <Field label="Last name *"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Role">
            <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v as typeof form.role_type })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["admin","principal","teacher","accountant","librarian","support"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Employment">
            <Select value={form.employment} onValueChange={(v) => setForm({ ...form, employment: v as typeof form.employment })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["full_time","part_time","contract","volunteer"].map((r) => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Staff number"><Input value={form.staff_number} onChange={(e) => setForm({ ...form, staff_number: e.target.value })} placeholder="Auto if blank" /></Field>
          <Field label="Hire date"><Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></Field>
          <Field label="Qualification" className="sm:col-span-2"><Input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} /></Field>
          <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (<div className={"space-y-1.5 " + (className ?? "")}><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
